import React, { useContext, useEffect, useState } from "react";
import { format, isSameDay, parseISO, set } from "date-fns";
import {
  Badge,
  Chip,
  IconButton,
  List,
  ListItem,
  ListItemSecondaryAction,
  ListItemText,
  makeStyles,
  Box,
  Tooltip,
  Avatar,
  Divider,
} from "@material-ui/core";

import { useHistory, useParams } from "react-router-dom";
import { AuthContext } from "../../context/Auth/AuthContext";

import DeleteIcon from "@material-ui/icons/Delete";
import EditIcon from "@material-ui/icons/Edit";
import PersonIcon from "@material-ui/icons/Person";

import ConfirmationModal from "../../components/ConfirmationModal";
import api from "../../services/api";
import toastError from "../../errors/toastError";

const useStyles = makeStyles((theme) => ({
  mainContainer: {
    display: "flex",
    flexDirection: "column",
    position: "relative",
    flex: 1,
    height: "calc(100% - 58px)",
    overflow: "hidden",
    borderRadius: 0,
    //backgroundColor: "inherit",
  },
  chatList: {
    display: "flex",
    flexDirection: "column",
    position: "relative",
    flex: 1,
    overflowY: "scroll",
    ...theme.scrollbarStyles,
  },
  listItem: {
    cursor: "pointer",
  },
  ownerChip: {
    height: 20,
    fontSize: "0.7rem",
    marginLeft: 8,
    backgroundColor: theme.palette.primary.main,
    color: "#fff",
  },
  ownerAvatar: {
    width: 16,
    height: 16,
    fontSize: "0.65rem",
    backgroundColor: theme.palette.primary.dark,
  },
  chatTitle: {
    flex: 1,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  titleContainer: {
    display: "flex",
    flexDirection: "column",
    width: "100%",
    overflow: "hidden",
    gap: 4,
  },
  ownerRow: {
    display: "flex",
    alignItems: "center",
    gap: 4,
  },
  titleRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    width: "100%",
  },
}));

export default function ChatList({
  chats,
  handleSelectChat,
  handleDeleteChat,
  handleEditChat,
  pageInfo,
  loading,
  onChatRead,
}) {
  const classes = useStyles();
  const history = useHistory();
  const { user } = useContext(AuthContext);

  const [confirmationModal, setConfirmModalOpen] = useState(false);
  const [selectedChat, setSelectedChat] = useState({});

  useEffect(() => {
    if (pageInfo && pageInfo.page === 1) {
      setSelectedChat({});
    }
  }, [pageInfo]);

  const goToMessages = async (chat) => {
    if (unreadMessages(chat) > 0) {
      const currentUser = chat.users.find((u) => u.userId === user.id);
      const ownerUser = chat.users.find((u) => u.ownerId === user.id);

      if (user.profile === "admin" && !currentUser && !ownerUser) {
        handleSelectChat(chat);
        history.push("/chats");
      } else {
        try {
          await api.post(`/chats/${chat.id}/read`, { userId: user.id });
          
          // Notificar componente pai que o chat foi lido
          if (onChatRead) {
            onChatRead(chat.id, user.id);
          }
          
          handleSelectChat(chat);
          history.push("/chats");
        } catch (err) {
          toastError(err);
        }
      }
    } else {
      handleSelectChat(chat);
      history.push("/chats");
    }
  };

  const handleDelete = () => {
    handleDeleteChat(selectedChat);
    setConfirmModalOpen(false);
  };

  const unreadMessages = (chat) => {
    if (chat && chat.users) {      
      const currentUser = chat.users.find((u) => u.userId === user.id);
      const ownerUser = chat.users.find((u) => u.ownerId === user.id);      
      if (currentUser) {
        return currentUser.unreads > 0;
      } else if (ownerUser) {
        return ownerUser.unreads > 0;
      }
    }

    // Se o usuário for admin, retornar 0 para unread messages, pois ele pode ver todos os chats
    if (user.profile === 'admin') {      
      for (let i = 0; i < chat.users.length; i++) {
        if (chat.users[i].unreads > 0) {
          return 1;
        }
        return 0;
      }
    }

    return 0;

  };

  const getPrimaryText = (chat) => {
    const mainText = chat.title;
    const unreads = unreadMessages(chat);
    const isOwner = chat.ownerId === user.id;
    const ownerName = chat.owner ? chat.owner.name : "";

    return (
      <Box className={classes.titleContainer}>
        <Box className={classes.titleRow}>
          {unreads > 0 && (
            <Box style={{ 
              width: 8, 
              height: 8, 
              borderRadius: '50%', 
              backgroundColor: '#f44336',
              marginRight: 8,
              flexShrink: 0
            }} />
          )}
          <Tooltip title={mainText}>
            <span className={classes.chatTitle}>{mainText}</span>
          </Tooltip>
        </Box>
        <Box className={classes.ownerRow}>
          {isOwner && (
            <Tooltip title="Você criou este chat">
              <Chip
                size="small"
                label="Você"
                className={classes.ownerChip}
                avatar={<Avatar className={classes.ownerAvatar}><PersonIcon style={{ fontSize: 12 }} /></Avatar>}
              />
            </Tooltip>
          )}
          {!isOwner && ownerName && (
            <Tooltip title={`Criado por ${ownerName}`}>
              <Chip
                size="small"
                label={ownerName.split(" ")[0]}
                style={{ 
                  height: 20, 
                  fontSize: "0.7rem",
                  backgroundColor: "#757575",
                  color: "#fff"
                }}
                avatar={<Avatar style={{ width: 16, height: 16, fontSize: "0.65rem", backgroundColor: "#616161" }}><PersonIcon style={{ fontSize: 12 }} /></Avatar>}
              />
            </Tooltip>
          )}
        </Box>
      </Box>
    );
  };

  const getSecondaryText = (chat) => {

    return chat.lastMessage !== "" && isSameDay(parseISO(chat.updatedAt), new Date()) ? (
      <>{format(parseISO(chat.updatedAt), "HH:mm")}</>
    ) : (
      <>{format(parseISO(chat.updatedAt), "dd/MM/yyyy")}</>
    )
  };

  const getItemStyle = (chat) => {
    // Lista de cores
    const colors = ["#002d6e", "#4caf50", "#ff9800", "#9c27b0", "#e91e63", "#FFDE59"];

    // Lógica para selecionar a cor com base no ID do chat
    const colorIndex = chat.id % colors.length; // Usa o resto da divisão pelo número de cores disponíveis

    return {
      borderLeft: chat.id === selectedChat.id ? `6px solid ${colors[colorIndex]}` : "6px solid #515151",
      backgroundColor: chat.id === selectedChat.id ? "#B3B5B1" : "inherit",
    };
  };


  return (
    <>
      <ConfirmationModal
        title={"Excluir Conversa"}
        open={confirmationModal}
        onClose={() => setConfirmModalOpen(false)}
        onConfirm={handleDelete}
      >
        Esta ação não pode ser revertida, confirmar?
      </ConfirmationModal>
      <div className={classes.mainContainer}>
        <div className={classes.chatList}>
          <List>
            {Array.isArray(chats) &&
              chats.length > 0 &&
              chats.map((chat, key) => (
                <React.Fragment key={key}>
                  <ListItem
                    onClick={() => {
                      goToMessages(chat);
                      setSelectedChat(chat);
                    }}
                    className={classes.listItem}
                    style={getItemStyle(chat)}
                    button
                  >
                    <ListItemText
                      primary={getPrimaryText(chat)}
                      secondary={getSecondaryText(chat)}
                    />
                    <ListItemSecondaryAction>
                    {(chat.ownerId === user.id || user.profile === "admin") && (
                      <IconButton
                        onClick={() => {
                          goToMessages(chat).then(() => {
                            handleEditChat(chat);
                          });
                        }}
                        edge="end"
                        aria-label="edit"
                        size="small"
                        style={{ marginRight: 5 }}
                      >
                        <EditIcon />
                      </IconButton>)}
                    {user.profile === "admin" && (
                      <IconButton
                        onClick={() => {
                          setSelectedChat(chat);
                          setConfirmModalOpen(true);
                        }}
                        edge="end"
                        aria-label="delete"
                        size="small"
                      >
                        <DeleteIcon />
                      </IconButton>)}
                  </ListItemSecondaryAction>
                </ListItem>
                {key < chats.length - 1 && <Divider />}
              </React.Fragment>
              ))}
          </List>
        </div>
      </div>
    </>
  );
}
